import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import {
  stErrorMessage,
  stFillI18n,
  StI18nEntry,
} from '../../services/socialTradingService';
import {
  applyFillResult,
  I18N_LANG_LABELS,
  I18N_LANGS,
  I18nLangCode,
  syncZh,
} from './i18nLang';
import { PermissionButton } from 'src/routes/components/permission-button';

type Props = {
  /** fillI18n field key (e.g. name / strategyName). */
  fieldKey: string;
  label: string;
  primary: string;
  onPrimaryChange: (value: string) => void;
  pack: StI18nEntry[];
  /** Pack changed via fill or manual locale edit (not zh sync from primary). */
  onPackChange: (pack: StI18nEntry[]) => void;
  onPrimaryEditedAfterFill?: () => void;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  helperText?: React.ReactNode;
  placeholder?: string;
  allowEmptyFill?: boolean;
};

export function I18nFieldBlock({
  fieldKey,
  label,
  primary,
  onPrimaryChange,
  pack,
  onPackChange,
  onPrimaryEditedAfterFill,
  required,
  multiline,
  rows = 2,
  helperText,
  placeholder,
  allowEmptyFill,
}: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [filling, setFilling] = useState(false);
  const [filledOnce, setFilledOnce] = useState(false);

  const setPackText = (lang: string, text: string) => {
    onPackChange(pack.map((e) => (e.lang === lang ? { ...e, text } : e)));
  };

  const handlePrimaryChange = (value: string) => {
    onPrimaryChange(value);
    if (filledOnce) onPrimaryEditedAfterFill?.();
  };

  const fill = async () => {
    const source = primary.trim();
    if (!source && !allowEmptyFill) {
      notify(t('module.socialTrading.i18nNeedPrimary'), { type: 'warning' });
      return;
    }
    setFilling(true);
    try {
      const results = await stFillI18n([{ key: fieldKey, source }]);
      const hit = results?.find((r) => r.key === fieldKey) || results?.[0];
      onPackChange(applyFillResult(pack, hit?.entries, primary));
      setFilledOnce(true);
      setOpen(true);
      notify(t('module.socialTrading.i18nFillOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setFilling(false);
    }
  };

  // zh display follows primary; other langs from pack
  const displayPack = syncZh(pack, primary);

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          label={`${label} (${t('module.socialTrading.i18nPrimaryZh')})`}
          required={required}
          value={primary}
          onChange={(e) => handlePrimaryChange(e.target.value)}
          multiline={multiline}
          rows={multiline ? rows : undefined}
          fullWidth
          helperText={helperText}
          placeholder={placeholder}
        />
        {/* POST /social-trading/integration/i18n/fill is guarded by st-agents:update on the BE */}
        <PermissionButton resource="st-agents" action="update">
          <Button
            variant="outlined"
            size="small"
            onClick={fill}
            disabled={filling}
            sx={{ flexShrink: 0, mt: { sm: 0.5 }, whiteSpace: 'nowrap' }}
          >
            {filling ? t('module.socialTrading.i18nFilling') : t('module.socialTrading.i18nAutoFill')}
          </Button>
        </PermissionButton>
      </Stack>
      <Box>
        <Button size="small" onClick={() => setOpen((v) => !v)}>
          {open ? t('module.socialTrading.i18nCollapse') : t('module.socialTrading.i18nExpand')}
        </Button>
        <Collapse in={open}>
          <Stack spacing={1} sx={{ mt: 1, pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('module.socialTrading.i18nPackHint')}
            </Typography>
            {I18N_LANGS.map((lang) => {
              const entry = displayPack.find((e) => e.lang === lang);
              const disabled = lang === 'zh';
              return (
                <TextField
                  key={lang}
                  size="small"
                  label={I18N_LANG_LABELS[lang as I18nLangCode]}
                  value={entry?.text ?? ''}
                  onChange={(e) => setPackText(lang, e.target.value)}
                  disabled={disabled}
                  multiline={multiline}
                  rows={multiline ? rows : undefined}
                  fullWidth
                  helperText={disabled ? t('module.socialTrading.i18nZhLocked') : undefined}
                />
              );
            })}
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );
}
